/* Fantastick Projekt-Carousel V1.0 – keine externe Slider-Bibliothek. */
(() => {
  'use strict';
  const tag = 'fantastick-project-carousel';
  if (customElements.get(tag)) return;
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const num = (value, fallback) => Number.isFinite(Number(value)) ? Number(value) : fallback;

  class FantastickProjectCarousel extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => { if (this.isConnected && !this.controller) this.init(); });
    }

    init() {
      const configNode = this.querySelector('[data-fpc-config]');
      if (!configNode) return;
      try { this.config = JSON.parse(configNode.textContent); } catch (_) { return; }
      this.cards = Array.from(this.querySelectorAll('[data-fpc-card]'));
      if (!this.cards.length) return;
      this.controller = new AbortController();
      this.frames = new Set();
      this.stage = this.querySelector('[data-fpc-stage]');
      this.dialog = this.querySelector('[data-fpc-dialog]');
      this.below = this.querySelector('[data-fpc-below]');
      this.prev = this.querySelector('[data-fpc-prev]');
      this.next = this.querySelector('[data-fpc-next]');
      this.count = this.querySelector('[data-fpc-count]');
      this.dots = this.querySelector('[data-fpc-dots]');
      this.filters = this.querySelector('[data-fpc-filters]');
      this.autoButton = this.querySelector('[data-fpc-autoplay]');
      this.status = this.querySelector('[data-fpc-status]');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.visible = this.cards.slice();
      this.index = 0;
      this.detail = null;
      this.pointer = null;
      this.suppressClickUntil = 0;
      this.hovered = false;
      this.onScreen = !('IntersectionObserver' in window);
      this.userPaused = false;
      this.timer = null;
      this.wheelSum = 0;
      this.lastWheel = 0;
      this.mode = ['flip','overlay','below','none'].includes(this.config.detailMode) ? this.config.detailMode : 'flip';
      if (this.mode === 'overlay' && typeof this.dialog.showModal !== 'function') this.mode = 'below';
      this.dataset.detailMode = this.mode;
      this.cards.forEach(card => {
        card._fpc = {
          front:card.querySelector('[data-fpc-front]'),
          flipper:card.querySelector('[data-fpc-flipper]'),
          details:card.querySelector('[data-fpc-details]'),
          pick:card.querySelector('[data-fpc-pick]'),
          open:card.querySelector('[data-fpc-open]'),
          distance:null
        };
        this.prepareInquiryLinks(card);
      });
      this.buildFilters();
      this.buildDots();
      this.bind();
      this.classList.add('is-ready');
      this.measure();
      this.render(false);
      this.ro = new ResizeObserver(() => { if (this.controller) { this.measure(); this.render(false); } });
      this.ro.observe(this.stage);
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(entries => {
          this.onScreen = entries.some(entry => entry.isIntersecting);
          this.schedule();
        }, {threshold:0.1});
        this.io.observe(this);
      }
      const help = this.querySelector('[data-fpc-help]');
      const gesture = this.config.swipe ? 'Wischen oder mit den Pfeilen stöbern.' : 'Mit den Pfeilen stöbern.';
      help.textContent = (this.config.navigation === 'none' ? (this.config.swipe ? 'Seitlich wischen, um weitere Projekte zu sehen.' : 'Seitliche Karte wählen oder die Pfeiltasten nutzen.') : gesture)
        + (this.mode === 'none' ? '' : ' Mittleres Projekt antippen für Details.');
      this.schedule();
    }

    listen(target, name, fn, options = {}) {
      if (target) target.addEventListener(name, fn, {...options, signal:this.controller.signal});
    }

    frame(fn) {
      const id = requestAnimationFrame(() => { this.frames.delete(id); if (this.controller && this.isConnected) fn(); });
      this.frames.add(id);
    }

    disconnectedCallback() {
      if (!this.controller) return;
      this.closeDetails(false);
      this.stop();
      this.controller.abort();
      this.controller = null;
      this.ro?.disconnect();
      this.io?.disconnect();
      this.frames.forEach(cancelAnimationFrame);
      this.frames.clear();
      this.classList.remove('is-ready','is-detail-open','is-dragging');
      this.cards.forEach(card => {
        card.hidden = false; card.inert = false; card.removeAttribute('aria-hidden');
        card._fpc.front.inert = false; card._fpc.flipper.inert = false; card._fpc.details.inert = false;
        card._fpc.front.removeAttribute('aria-hidden'); card._fpc.details.removeAttribute('aria-hidden');
      });
    }

    prepareInquiryLinks(card) {
      card.querySelectorAll('[data-fpc-inquiry]').forEach(link => {
        try {
          const url = new URL(link.getAttribute('href'), location.href);
          if (!['http:','https:'].includes(url.protocol)) return;
          url.searchParams.set('reference', card.dataset.project);
          link.href = url.origin === location.origin ? url.pathname + url.search + url.hash : url.href;
        } catch (_) { /* Ursprünglicher Link bleibt nutzbar. */ }
      });
    }

    measure() {
      const width = this.stage.clientWidth;
      const mobile = matchMedia('(max-width: 749px)').matches;
      this.cardWidth = mobile ? width * clamp(num(this.config.mobileWidth,82),65,90)/100 : Math.min(num(this.config.desktopWidth,340),width*0.74);
      const ratio = this.config.ratio === '3_4' ? 4/3 : this.config.ratio === 'custom'
        ? clamp(num(this.config.customHeight,5)/num(this.config.customWidth,4),0.5,4) : 5/4;
      this.cardHeight = this.cardWidth * ratio;
      this.radius = Math.min(mobile ? num(this.config.mobileSideCards,1) : num(this.config.sideCards,2),Math.floor(this.visible.length/2));
      this.style.setProperty('--fpc-card-width', this.cardWidth+'px');
      this.style.setProperty('--fpc-card-height', this.cardHeight+'px');
      this.style.setProperty('--fpc-stage-height', (this.cardHeight+64+Math.abs(num(this.config.offsetY,8))*this.radius)+'px');
    }

    distance(index) {
      let d = index-this.index;
      if (this.config.loop && this.visible.length > 1) {
        const n=this.visible.length;
        d=((d+Math.floor(n/2))%n+n)%n-Math.floor(n/2);
      }
      return d;
    }

    hydrate(card, active) {
      const template = card.querySelector('[data-fpc-image-template]');
      if (template) template.replaceWith(template.content.cloneNode(true));
      const image=card.querySelector('.fpc__image');
      if (image) { image.draggable=false; image.sizes=Math.ceil(this.cardWidth)+'px'; if (active) image.loading='eager'; }
    }

    render(announce = true) {
      const active = this.visible[this.index];
      this.cards.forEach(card => {
        const index = this.visible.indexOf(card);
        if (index < 0) { card.hidden=true; card.inert=true; card.setAttribute('aria-hidden','true'); return; }
        card.hidden=false;
        const d=this.distance(index), distance=Math.abs(d), isActive=card===active;
        const shown=distance<=this.radius || isActive;
        const old=card._fpc.distance;
        if (!this.reduced.matches && old!==null && old!==0 && d!==0 && Math.sign(old)!==Math.sign(d) && Math.abs(old-d)>1 && shown) {
          // Die äußerste Karte wird hinter dem sichtbaren Stapel zyklisch umgesetzt.
          // Es werden keine Karten geklont; die DOM-Anzahl bleibt konstant.
          card.classList.add('is-recycling');
          this.frame(()=>this.frame(()=>card.classList.remove('is-recycling')));
        }
        card._fpc.distance=d;
        card.classList.toggle('is-active',isActive);
        card.classList.toggle('is-offstage',!shown);
        card.inert=!shown;
        card.setAttribute('aria-hidden',String(!isActive));
        card.setAttribute('aria-roledescription','Projektkarte');
        card._fpc.flipper.inert=!isActive;
        card._fpc.pick.disabled=!shown || isActive || Boolean(this.detail);
        const open=isActive && Boolean(this.detail);
        card._fpc.front.inert=open;
        card._fpc.front.setAttribute('aria-hidden',String(open));
        card._fpc.details.inert=!open;
        card._fpc.details.setAttribute('aria-hidden',String(!open));
        card._fpc.open.setAttribute('aria-expanded',String(open));
        card.style.setProperty('--fpc-x',(d*this.cardWidth*num(this.config.spread,50)/100)+'px');
        card.style.setProperty('--fpc-y',(distance*num(this.config.offsetY,8))+'px');
        card.style.setProperty('--fpc-z',(-distance*num(this.config.depth,45))+'px');
        card.style.setProperty('--fpc-angle',(-d*num(this.config.rotation,12))+'deg');
        card.style.setProperty('--fpc-scale',String(Math.pow(num(this.config.backScale,94)/100,distance)));
        card.style.setProperty('--fpc-opacity',String(Math.pow(num(this.config.opacity,85)/100,distance)));
        card.style.setProperty('--fpc-blur',(distance*num(this.config.blur,1))+'px');
        card.style.setProperty('--fpc-z-index',String(100-distance*10));
        if (distance<=this.radius+1) this.hydrate(card,isActive);
      });
      const n=this.visible.length;
      this.count.textContent=(n?this.index+1:0)+' / '+n;
      this.prev.disabled=Boolean(this.detail)||n<2||(!this.config.loop&&this.index===0);
      this.next.disabled=Boolean(this.detail)||n<2||(!this.config.loop&&this.index===n-1);
      const arrows=['arrows','both'].includes(this.config.navigation);
      this.prev.hidden=this.next.hidden=!arrows||n<2;
      this.autoButton.hidden=!this.config.autoplay||this.config.designMode||this.reduced.matches||n<2;
      this.autoButton.textContent=this.userPaused?'Automatik starten':'Automatik pausieren';
      this.autoButton.setAttribute('aria-pressed',String(this.userPaused));
      this.querySelector('[data-fpc-navigation]').hidden=n<2;
      this.dots.hidden=!['dots','both'].includes(this.config.navigation)||n<2;
      this.dots.querySelectorAll('button').forEach((button,i)=>{
        button.setAttribute('aria-current',String(i===this.index));
        button.disabled=Boolean(this.detail);
      });
      this.filters.querySelectorAll('button').forEach(button=>{button.disabled=Boolean(this.detail);});
      if (announce && active) this.status.textContent='Projekt '+(this.index+1)+' von '+n+': '+active.querySelector('.fpc__title').textContent.trim();
    }

    select(index, announce = true) {
      if (this.detail || !this.visible.length) return;
      const n=this.visible.length;
      const next=this.config.loop?((index%n)+n)%n:clamp(index,0,n-1);
      const active=this.visible[this.index];
      // Tastaturfokus darf nicht in einer anschließend ausgeblendeten Karte bleiben.
      if (active?.contains(document.activeElement)) this.stage.focus({preventScroll:true});
      this.index=next;
      this.render(announce);
      this.schedule();
    }

    move(direction, announce = true) { this.select(this.index+direction,announce); }

    buildFilters() {
      this.filters.replaceChildren();
      const categories=[...new Set(this.cards.map(card=>card.dataset.category).filter(Boolean))];
      this.filters.hidden=!this.config.filters||categories.length<2;
      if (this.filters.hidden) return;
      ['',...categories].forEach(category=>{
        const button=document.createElement('button');
        button.type='button';button.className='fpc__filter';button.dataset.fpcCategory=category;
        button.textContent=category||'Alle';button.setAttribute('aria-pressed',String(!category));
        this.filters.append(button);
      });
    }

    filter(category) {
      if (this.detail) return;
      this.visible=this.cards.filter(card=>!category||card.dataset.category===category);
      this.index=0;
      this.cards.forEach(card=>{card._fpc.distance=null;});
      this.filters.querySelectorAll('button').forEach(button=>button.setAttribute('aria-pressed',String(button.dataset.fpcCategory===category)));
      this.buildDots();this.measure();this.render();this.schedule();
    }

    buildDots() {
      this.dots.replaceChildren();
      this.visible.forEach((card,index)=>{
        const button=document.createElement('button');
        button.type='button';button.className='fpc__dot';button.dataset.fpcIndex=String(index);
        button.setAttribute('aria-label','Projekt '+(index+1)+': '+card.querySelector('.fpc__title').textContent.trim());
        this.dots.append(button);
      });
    }

    openDetails() {
      if (this.detail||this.mode==='none') return;
      const card=this.visible[this.index]; if (!card) return;
      const body=card._fpc.details;
      this.detail={card,body,parent:body.parentNode,next:body.nextSibling,opener:card._fpc.open};
      this.stop();this.classList.add('is-detail-open');
      if (this.mode==='flip') card.classList.add('is-flipped');
      if (this.mode==='below') {this.below.hidden=false;this.below.append(body);}
      if (this.mode==='overlay') {
        this.querySelector('[data-fpc-dialog-panel]').append(body);
        this.dialog.setAttribute('aria-labelledby',body.querySelector('h3').id);
      }
      this.render(false);
      try { if (this.mode==='overlay') this.dialog.showModal(); }
      catch (_) { this.closeDetails();return; }
      body.querySelector('[data-fpc-close]').focus({preventScroll:true});
      if (this.mode==='below') this.below.scrollIntoView({block:'nearest',behavior:this.reduced.matches?'auto':'smooth'});
    }

    closeDetails(restore = true) {
      if (!this.detail) return;
      const {card,body,parent,next,opener}=this.detail;
      this.detail=null;
      if (this.dialog.open) this.dialog.close();
      parent.insertBefore(body,next?.parentNode===parent?next:null);
      this.below.hidden=true;card.classList.remove('is-flipped');this.classList.remove('is-detail-open');
      this.render(false);
      if (restore&&opener.isConnected) opener.focus({preventScroll:true});
      this.schedule();
    }

    stop() { clearTimeout(this.timer);this.timer=null; }

    schedule() {
      this.stop();
      if (!this.controller||!this.config.autoplay||this.config.designMode||this.userPaused||this.reduced.matches||!this.onScreen||document.hidden||this.hovered||this.detail||this.pointer||this.contains(document.activeElement)||this.visible.length<2) return;
      if (!this.config.loop&&this.index===this.visible.length-1) return;
      this.timer=setTimeout(()=>this.move(1,false),clamp(num(this.config.interval,7000),3000,10000));
    }

    bind() {
      this.listen(this.prev,'click',()=>this.move(-1));
      this.listen(this.next,'click',()=>this.move(1));
      this.listen(this.dots,'click',event=>{const button=event.target.closest('[data-fpc-index]');if(button)this.select(Number(button.dataset.fpcIndex));});
      this.listen(this.filters,'click',event=>{const button=event.target.closest('[data-fpc-category]');if(button)this.filter(button.dataset.fpcCategory);});
      this.listen(this.autoButton,'click',()=>{this.userPaused=!this.userPaused;this.render(false);this.schedule();});
      this.listen(this,'click',event=>{
        if(Date.now()<this.suppressClickUntil){event.preventDefault();event.stopPropagation();return;}
        if(event.target.closest('[data-fpc-close]')){this.closeDetails();return;}
        if(this.detail)return;
        const card=event.target.closest('[data-fpc-card]');if(!card||!this.contains(card))return;
        if(event.target.closest('[data-fpc-pick]')){this.select(this.visible.indexOf(card));return;}
        if(card.classList.contains('is-active')&&event.target.closest('[data-fpc-front]')&&!event.target.closest('a'))this.openDetails();
      });
      this.listen(document,'click',event=>{
        if (!this.detail||this.mode==='overlay')return;
        const inside=event.composedPath();
        if (!inside.includes(this.detail.card)&&!inside.includes(this.detail.body)) this.closeDetails(false);
      });
      this.listen(this.stage,'keydown',event=>{
        if(this.detail){if(event.key==='Escape'){event.preventDefault();this.closeDetails();}return;}
        if(event.target.closest('input,textarea,select'))return;
        const actions={ArrowLeft:()=>this.move(-1),ArrowRight:()=>this.move(1),Home:()=>this.select(0),End:()=>this.select(this.visible.length-1)};
        if(actions[event.key]){event.preventDefault();actions[event.key]();}
      });
      this.listen(this,'keydown',event=>{if(event.key==='Escape'&&this.detail){event.preventDefault();this.closeDetails();}});
      this.listen(this.dialog,'cancel',event=>{event.preventDefault();this.closeDetails();});
      this.listen(this.dialog,'close',()=>{if(!this.dialog.open)this.closeDetails();});
      let backdropDown=false;
      this.listen(this.dialog,'pointerdown',event=>{backdropDown=event.target===this.dialog;});
      this.listen(this.dialog,'click',event=>{if(backdropDown&&event.target===this.dialog)this.closeDetails();backdropDown=false;});
      this.listen(this,'pointerenter',event=>{if(event.pointerType==='mouse'){this.hovered=true;this.stop();}});
      this.listen(this,'pointerleave',event=>{if(event.pointerType==='mouse'){this.hovered=false;this.schedule();}});
      this.listen(this,'focusin',()=>this.stop());
      this.listen(this,'focusout',()=>this.frame(()=>this.schedule()));
      this.listen(document,'visibilitychange',()=>this.schedule());
      this.listen(this.reduced,'change',()=>{this.render(false);this.schedule();});
      this.listen(this.stage,'pointerdown',event=>{
        if(this.detail||this.visible.length<2||!event.isPrimary||(event.pointerType==='mouse'&&event.button!==0)||event.target.closest('a'))return;
        if(event.pointerType==='mouse'?!this.config.drag:!this.config.swipe)return;
        this.pointer={id:event.pointerId,x:event.clientX,y:event.clientY,axis:null};this.stop();
      },{passive:true});
      this.listen(this.stage,'pointermove',event=>{
        const p=this.pointer;if(!p||event.pointerId!==p.id)return;
        const x=Math.abs(event.clientX-p.x),y=Math.abs(event.clientY-p.y);
        if(!p.axis&&Math.max(x,y)>8){
          if(y>x*1.15){this.pointer=null;this.schedule();return;}
          if(x>y*1.15){p.axis='x';this.stage.setPointerCapture(event.pointerId);this.classList.add('is-dragging');}
        }
        if(p.axis==='x'&&event.cancelable)event.preventDefault();
      },{passive:false});
      const finish=(event,cancelled=false)=>{
        const p=this.pointer;if(!p||p.id!==event.pointerId)return;
        this.pointer=null;this.classList.remove('is-dragging');
        if(this.stage.hasPointerCapture(event.pointerId))this.stage.releasePointerCapture(event.pointerId);
        if(p.axis==='x'){
          this.suppressClickUntil=Date.now()+400;
          const dx=event.clientX-p.x;
          if(!cancelled&&Math.abs(dx)>Math.min(60,this.stage.clientWidth*.12))this.move(dx<0?1:-1);
        }
        this.schedule();
      };
      this.listen(this.stage,'pointerup',event=>finish(event));
      this.listen(this.stage,'pointercancel',event=>finish(event,true));
      this.listen(this.stage,'lostpointercapture',event=>{
        // Touch startet mit impliziter Capture am angetippten Bild. Deren Ende
        // beim Wechsel zur Stage ist kein Abbruch unserer Wischgeste.
        if(event.target===this.stage&&!this.stage.hasPointerCapture(event.pointerId))finish(event,true);
      });
      this.listen(window,'pointerup',event=>finish(event));
      this.listen(this.stage,'wheel',event=>{
        if(!this.config.shiftWheel||!event.shiftKey||this.detail||this.visible.length<2)return;
        event.preventDefault();
        const now=Date.now();if(now-this.lastWheel>220)this.wheelSum=0;
        this.wheelSum+=event.deltaX||event.deltaY;
        if(Math.abs(this.wheelSum)>45&&now-this.lastWheel>220){this.move(this.wheelSum>0?1:-1);this.lastWheel=now;this.wheelSum=0;}
      },{passive:false});
    }
  }
  customElements.define(tag,FantastickProjectCarousel);
})();
